import { supabase } from '../lib/supabase';

/**
 * Service for managing product and component reviews with admin moderation
 */
export const reviewService = {
  /**
   * Get all reviews (products + components) with filtering and pagination
   */
  async getAllReviews({ status, type, sortBy = 'created_at', order = 'desc', limit = 50, offset = 0 } = {}) {
    try {
      let allReviews = [];

      if (!type || type === 'product') {
        let productQuery = supabase?.from('product_ratings')?.select(`
            *,
            product:products(id, name, thumbnail_path),
            reviewer:users!product_ratings_user_id_fkey(id, first_name, last_name, email),
            moderator:users!product_ratings_moderated_by_fkey(id, first_name, last_name)
          `);
        
        if (status) productQuery = productQuery?.eq('status', status);
        productQuery = productQuery?.order(sortBy, { ascending: order === 'asc' });

        const { data: productData, error: productError } = await productQuery;
        if (productError) {
          console.error('Error fetching product reviews:', productError);
        } else {
          allReviews = [...allReviews, ...productData?.map(r => ({
            ...this.toCamelCase(r),
            reviewType: 'product',
            itemName: r?.product?.name,
            itemImage: r?.product?.thumbnail_path
          }))];
        }
      }

      if (!type || type === 'component') {
        let componentQuery = supabase?.from('component_ratings')?.select(`
            *,
            seller_components(id, custom_name, components(name)),
            reviewer:users!component_ratings_user_id_fkey(id, first_name, last_name, email),
            moderator:users!component_ratings_moderated_by_fkey(id, first_name, last_name)
          `);
        
        if (status) componentQuery = componentQuery?.eq('status', status);
        componentQuery = componentQuery?.order(sortBy, { ascending: order === 'asc' });

        const { data: componentData, error: componentError } = await componentQuery;
        if (componentError) {
          console.error('Error fetching component reviews:', componentError);
        } else {
          allReviews = [...allReviews, ...componentData?.map(r => ({
            ...this.toCamelCase(r),
            reviewType: 'component',
            itemName: r?.seller_components?.custom_name || r?.seller_components?.components?.name || 'Component',
            itemImage: null
          }))];
        }
      }

      // Sort combined results (if both types are fetched)
      if (!type) { // Only sort if we combined product and component reviews
        allReviews?.sort((a, b) => {
          const aVal = a?.[this.snakeToCamelCase(sortBy)];
          const bVal = b?.[this.snakeToCamelCase(sortBy)];
          if (order === 'asc') return aVal > bVal ? 1 : -1;
          return aVal < bVal ? 1 : -1;
        });
      }

      // Paginate
      const paginatedReviews = allReviews?.slice(offset, offset + limit);

      return {
        reviews: paginatedReviews,
        total: allReviews?.length
      };
    } catch (error) {
      console.error('Error fetching reviews:', error);
      throw error;
    }
  },

  /**
   * Get review analytics
   */
  async getAnalytics(type = null) { // Add type parameter
    try {
      let query = supabase?.from('review_analytics')?.select('*');
      
      if (type) {
        query = query?.eq('review_type', type); // Use 'review_type' column from the view
      }

      const { data, error } = await query;
      if (error) throw error;
      
      if (data && data.length > 0) {
        return this.toCamelCase(data[0]); // Return the single camelCased object
      }
      return null;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  },

  /**
   * Moderate a review (approve/reject/flag)
   */
  async moderateReview(reviewId, reviewType, status, moderationNotes) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const table = reviewType === 'product' ? 'product_ratings' : 'component_ratings';
      
      const { error } = await supabase?.from(table)?.update({ // Removed .select()
          status,
          moderated_by: user?.id,
          moderated_at: new Date()?.toISOString(),
          moderation_notes: moderationNotes
        })?.eq('id', reviewId);
      
      if (error) throw error;
      
      // Since .select() is removed, we just return a success indicator
      return { success: true, reviewId, reviewType, status };
    } catch (error) {
      console.error('Error moderating review:', error);
      throw error;
    }
  },

  /**
   * Delete a review
   */
  async deleteReview(reviewId, reviewType) {
    try {
      const table = reviewType === 'product' ? 'product_ratings' : 'component_ratings';
      
      const { error } = await supabase?.from(table)?.delete()?.eq('id', reviewId);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error deleting review:', error);
      throw error;
    }
  },

  async updateReview(reviewId, reviewType, updatedData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let table;
      if (reviewType === 'Product') {
        table = 'product_ratings';
      } else if (reviewType === 'Component') {
        table = 'component_ratings';
      } else if (reviewType === 'Seller') {
        table = 'seller_ratings';
      } else {
        throw new Error('Invalid review type provided.');
      }

      const snakeCaseData = {
        rating: updatedData.rating,
        review: updatedData.comment, // Use 'review' as the column name
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        ?.from(table)
        ?.update(snakeCaseData)
        ?.eq('id', reviewId)
        ?.eq('user_id', user.id) // Ensure only the owner can update
        ?.select();

      if (error) throw error;
      if (!data || data.length === 0) throw new Error('Review not found or not authorized to update.');

      return this.toCamelCase(data[0]);
    } catch (error) {
      console.error('Error updating review:', error);
      throw error;
    }
  },

  /**
   * Feature/unfeature a review
   */
  async toggleFeatured(reviewId, reviewType, isFeatured) {
    try {
      const table = reviewType === 'product' ? 'product_ratings' : 'component_ratings';
      
      const { data, error } = await supabase?.from(table)?.update({ is_featured: isFeatured })?.eq('id', reviewId)?.select();
      
      if (error) throw error;
      
      // Check if any rows were actually updated
      if (!data || data?.length === 0) {
        throw new Error(`Review not found in ${table} table with id: ${reviewId}`);
      }
      
      return this.toCamelCase(data?.[0]);
    } catch (error) {
      console.error('Error toggling featured status:', error);
      throw error;
    }
  },

  /**
   * Get reports for a specific review
   */
  async getReviewReports(reviewId, reviewType) {
    try {
      const { data, error } = await supabase?.from('review_reports')?.select(`
          *,
          reporter:users(id, first_name, last_name, email)
        `)?.eq('review_type', reviewType)?.eq('review_id', reviewId)?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data?.map(r => this.toCamelCase(r));
    } catch (error) {
      console.error('Error fetching reports:', error);
      throw error;
    }
  },

  /**
   * Report a review
   */
  async reportReview(reviewId, reviewType, reason, description) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('review_reports')?.insert({
          review_type: reviewType,
          review_id: reviewId,
          reporter_id: user?.id,
          reason,
          description
        })?.select()?.single();
      
      if (error) throw error;
      
      return this.toCamelCase(data);
    } catch (error) {
      console.error('Error reporting review:', error);
      throw error;
    }
  },
/**
 * Bulk moderation actions
 */
async bulkModerate(reviewIds, reviewType, status, moderationNotes) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const table = reviewType?.toLowerCase() === 'product' ? 'product_ratings' : 
                  reviewType?.toLowerCase() === 'component' ? 'component_ratings' : 'seller_ratings';

    const { data, error } = await supabase?.from(table)?.update({
        status,
        moderated_by: user?.id,
        moderated_at: new Date()?.toISOString(),
        moderation_notes: moderationNotes
      })?.in('id', reviewIds)?.select();

    if (error) throw error;

    return data?.map(r => this.toCamelCase(r));
  } catch (error) {
    console.error('Error bulk moderating:', error);
    throw error;
  }
},

/**
 * Respond to a review as a seller
 */
async respondToReview(reviewId, reviewType, responseText) {
  try {
    const table = reviewType?.toLowerCase() === 'product' ? 'product_ratings' : 
                  reviewType?.toLowerCase() === 'component' ? 'component_ratings' : 'seller_ratings';

    const { data, error } = await supabase?.from(table)?.update({
        seller_response: responseText,
        seller_response_at: new Date().toISOString()
      })?.eq('id', reviewId)?.select();

    if (error) throw error;

    return this.toCamelCase(data?.[0]);
  } catch (error) {
    console.error('Error responding to review:', error);
    throw error;
  }
},

  /**
   * Get all reviews for a specific seller
   */
  async getReviewsForSeller(sellerId) { // sellerId here is user.id of the current seller
    if (!sellerId) {
      console.error("Seller ID is required to fetch reviews.");
      return [];
    }

    try {
      let allReviews = [];

      // 1. Get seller_account.id for the current user (sellerId is user.id)
      const { data: sellerAccount, error: sellerAccountError } = await supabase
        .from('seller_accounts')
        .select('id, business_name') // Also get business_name for reviewedItemName for seller reviews
        .eq('user_id', sellerId) // Link user.id to seller_accounts.user_id
        .single();

      if (sellerAccountError || !sellerAccount) {
        console.error(`Error fetching seller account for user ${sellerId}:`, sellerAccountError?.message || 'Seller account not found.');
        // If no seller account, we can't fetch seller-specific reviews, but can still fetch product/component reviews by the seller
        // So, do not throw an error here, just proceed.
      }
      const sellerAccountId = sellerAccount?.id;
      const sellerBusinessName = sellerAccount?.business_name;

      // 2. Fetch all products created by the seller (using user.id)
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('id, name') // Also get name for reviewedItemName
        .eq('created_by', sellerId); // created_by is user.id

      if (productsError) {
        console.error(`Error fetching products for seller ${sellerId}:`, productsError);
        throw productsError;
      }
      const productIds = products.map(p => p.id);

      // 3. Fetch all components created by the seller (using user.id)
      const { data: components, error: componentsError } = await supabase
        .from('components')
        .select('id, name') // Also get name for reviewedItemName
        .eq('created_by', sellerId); // created_by is user.id

      if (componentsError) {
        console.error(`Error fetching components for seller ${sellerId}:`, componentsError);
        throw componentsError;
      }
      const componentIds = components.map(c => c.id);

      // 4. Fetch reviews for the seller's products
      if (productIds.length > 0) {
        const { data: productReviews, error: productReviewsError } = await supabase
          .from('product_ratings')
          .select(`
            id, rating, review, created_at, product_id, user_id, status, seller_response, seller_response_at,
            reviewer:users!product_ratings_user_id_fkey(id, first_name, last_name, email),
            product:products(name)
          `)
          .in('product_id', productIds);

        if (productReviewsError) {
          console.error(`Error fetching product reviews for seller ${sellerId}:`, productReviewsError);
        } else {
          allReviews = allReviews.concat(productReviews.map(r => ({
            ...this.toCamelCase(r),
            reviewType: 'Product',
            reviewedItemName: r.product.name
          })));
        }
      }

      // 5. Fetch reviews for the seller's components
      if (componentIds.length > 0) {
        const { data: componentReviews, error: componentReviewsError } = await supabase
          .from('component_ratings')
          .select(`
            id, rating, review, created_at, component_id, user_id, status, seller_response, seller_response_at,
            reviewer:users!component_ratings_user_id_fkey(id, first_name, last_name, email),
            seller_components(custom_name, components(name))
          `)
          .in('component_id', componentIds);

        if (componentReviewsError) {
          console.error(`Error fetching component reviews for seller ${sellerId}:`, componentReviewsError);
        } else {
          allReviews = allReviews.concat(componentReviews.map(r => ({
            ...this.toCamelCase(r),
            reviewType: 'Component',
            reviewedItemName: r.seller_components?.custom_name || r.seller_components?.components?.name || 'Unknown Component'
          })));
        }
      }
      
      // 6. Fetch reviews FOR THE SELLER (about their store)
      if (sellerAccountId) {
          const { data: sellerReviews, error: sellerReviewsError } = await supabase
            .from('seller_ratings')
            .select(`
                id, rating, review, created_at, seller_id, user_id, status, seller_response, seller_response_at,
                reviewer:users!seller_ratings_user_id_fkey(id, first_name, last_name, email),
                seller:seller_accounts(business_name)
            `)
            .eq('seller_id', sellerAccountId); // Join with seller_accounts.id

          if (sellerReviewsError) {
            console.error(`Error fetching seller reviews for seller account ${sellerAccountId}:`, sellerReviewsError);
          } else {
            allReviews = allReviews.concat(sellerReviews.map(r => ({
              ...this.toCamelCase(r),
              reviewType: 'Seller',
              reviewedItemName: sellerBusinessName // Use the fetched business name as reviewed item name
            })));
          }
      }

      // Sort reviews by creation date
      allReviews.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      return allReviews;
    } catch (error) {
      console.error(`Failed to get reviews for seller ${sellerId}:`, error);
      throw error;
    }
  },


  /**
   * Get all reviews for a specific user
   */
  async getReviewsForUser(userId) {
    if (!userId) {
      console.error("User ID is required to fetch reviews.");
      return [];
    }

    try {
      let allReviews = [];

      // Fetch product reviews
      const { data: productReviews, error: productReviewsError } = await supabase
        .from('product_ratings')
        .select('id, rating, review, created_at, product_id, user_id, status, product:products(name)')
        .eq('user_id', userId);

      if (productReviewsError) {
        console.error(`Error fetching product reviews for user ${userId}:`, productReviewsError);
      } else {
        allReviews = allReviews.concat(productReviews.map(r => ({
          ...this.toCamelCase(r),
          reviewType: 'Product',
          reviewedItemName: r.product.name
        })));
      }

      // Fetch component reviews
      const { data: componentReviews, error: componentReviewsError } = await supabase
        .from('component_ratings')
        .select('id, rating, review, created_at, component_id, user_id, status, seller_components(custom_name, components(name))')
        .eq('user_id', userId);

      if (componentReviewsError) {
        console.error(`Error fetching component reviews for user ${userId}:`, componentReviewsError);
      } else {
        allReviews = allReviews.concat(componentReviews.map(r => ({
          ...this.toCamelCase(r),
          reviewType: 'Component',
          reviewedItemName: r.seller_components?.custom_name || r.seller_components?.components?.name || 'Unknown Component'
        })));
      }

      // Fetch seller reviews
      const { data: sellerReviews, error: sellerReviewsError } = await supabase
        .from('seller_ratings')
        .select('id, rating, review, created_at, seller_id, user_id, status, seller:seller_accounts(business_name)')
        .eq('user_id', userId);

      if (sellerReviewsError) {
        console.error(`Error fetching seller reviews for user ${userId}:`, sellerReviewsError);
      } else {
        allReviews = allReviews.concat(sellerReviews.map(r => ({
          ...this.toCamelCase(r),
          reviewType: 'Seller',
          reviewedItemName: r.seller.business_name
        })));
      }

      // Sort reviews by creation date
      allReviews.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      return allReviews;
    } catch (error) {
      console.error(`Failed to get reviews for user ${userId}:`, error);
      throw error;
    }
  },

  // Helper methods for case conversion
  toCamelCase(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    
    // Handle arrays
    if (Array.isArray(obj)) {
      return obj?.map(item => this.toCamelCase(item));
    }
    
    const camelObj = {};
    for (const key in obj) {
      if (obj?.hasOwnProperty(key)) {
        const camelKey = this.snakeToCamelCase(key);
        const value = obj?.[key];
        
        // Recursively convert nested objects and arrays
        if (value && typeof value === 'object') {
          camelObj[camelKey] = this.toCamelCase(value);
        } else {
          camelObj[camelKey] = value;
        }
      }
    }
    return camelObj;
  },

  snakeToCamelCase(str) {
    return str?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
  },
};